/**
 * MonthYearPicker — Feb-2026 Iter 28
 *
 * Komfortable Alternative zu `<input type="month">` (Native-Picker versteckt
 * Jahre, wenn Tag-/Monatsauswahl angezeigt wird).
 *
 * Speichert das Datum im ISO-Monatsformat „YYYY-MM" (kompatibel zum bisherigen
 * Backend-Schema — kein Migration-Aufwand).
 *
 * Props:
 *   value       – string „YYYY-MM" oder ""
 *   onChange    – (newValue: string) => void
 *   yearsBack   – wieviele Jahre VOR diesem Jahr im Picker (default 30)
 *   yearsAhead  – wieviele Jahre NACH diesem Jahr (default 10)
 *   testId      – data-testid Präfix
 */
import { useMemo, useState, useEffect } from "react";

export default function MonthYearPicker({
    value = "",
    onChange,
    yearsBack = 30,
    yearsAhead = 10,
    testId,
    disabled = false,
}) {
    const thisYear = new Date().getFullYear();
    const years = useMemo(
        () => Array.from({ length: yearsBack + yearsAhead + 1 }, (_, i) => thisYear + yearsAhead - i),
        [thisYear, yearsBack, yearsAhead],
    );
    // Lokaler Zustand für Monat/Jahr — hält auch eine Teilauswahl fest (nur Monat
    // ODER nur Jahr), die zuvor durch onChange("") verworfen wurde (→ Auswahl
    // verschwand). Erst wenn BEIDE gesetzt sind, wird "YYYY-MM" nach oben gemeldet.
    const [mm, setMm] = useState("");
    const [yy, setYy] = useState("");
    useEffect(() => {
        const [y, m] = (value || "").split("-");
        setYy(y ? String(parseInt(y, 10)) : "");
        setMm(m ? String(parseInt(m, 10)) : "");
    }, [value]);

    const emit = (newMonth, newYear) => {
        setMm(newMonth);
        setYy(newYear);
        if (newMonth && newYear) {
            onChange(`${newYear}-${String(newMonth).padStart(2, "0")}`);
        } else {
            onChange("");
        }
    };

    return (
        <div className="flex gap-2" data-testid={testId}>
            <select
                value={mm}
                onChange={(e) => emit(e.target.value, yy)}
                disabled={disabled}
                className="flex-1 h-9 px-2 border border-gray-300 rounded-sm bg-white text-sm font-mono-num focus:border-[#111827] focus:outline-none"
                data-testid={testId ? `${testId}-month` : undefined}
                aria-label="Monat"
            >
                <option value="">Monat …</option>
                {Array.from({ length: 12 }, (_, i) => i + 1).map((n) => (
                    <option key={n} value={n}>{String(n).padStart(2, "0")}</option>
                ))}
            </select>
            <select
                value={yy}
                onChange={(e) => emit(mm, e.target.value)}
                disabled={disabled}
                className="flex-1 h-9 px-2 border border-gray-300 rounded-sm bg-white text-sm font-mono-num focus:border-[#111827] focus:outline-none"
                data-testid={testId ? `${testId}-year` : undefined}
                aria-label="Jahr"
            >
                <option value="">Jahr …</option>
                {years.map((y) => (
                    <option key={y} value={y}>{y}</option>
                ))}
            </select>
        </div>
    );
}
